import React, { useEffect, useState } from 'react';
import { Image } from './image';
import TagManager from 'react-gtm-module';
import { OnOffSwitch } from '../OnOffSwitch';
import { motion, AnimatePresence } from 'framer-motion';
import style from './athlete-profile-lite.module.scss';
import { CountryFlag } from '../../Utils/flags';
import athlete_skeleton from './athlete_skeleton.png';
import { AppContext } from '../../App';
import { getTrans } from '../../Utils/translate';
import { getAthleteIdById } from '../../Utils/crosslink';
import { CosmosButton, CosmosIconArrowLeft, CosmosIconArrowRight, CosmosImage, CosmosText } from '../../cosmos/web-scoped/react';
import { ModalSelect } from '../Modal/select';
import { capitalize } from '../../Utils/string';

let iwdTimeout = undefined;

const AthleteProfileLite = ({ config }) => {
  const context = React.useContext(AppContext);
  const [selected, setSelected] = useState();
  const [options, setOptions] = useState();
  const [active, setActive] = useState(true);

  const [athleteId, setAthleteId] = useState();
  const [entrants, setEntrants] = useState();
  const [[page, direction], setPage] = useState([0, 0]);
  const [arrow, setArrow] = useState(false);
  const { selectText, autoUpdate } = context.lang;

  const fetchActive = async (active) => {
    if (!entrants) {
      return false;
    }

    clearTimeout(iwdTimeout);
    iwdTimeout = undefined;

    // preset first athlete
    let json = {
      active: [{ athlete_id: entrants[0].athlete_id || entrants[0].athlete.athlete_id || entrants[0].athlete.id }],
    }

    // use active athlete if configured
    if (config.activeJsonUri) {
      json = await fetch(config.activeJsonUri, { credentials: 'include' })
        .then((data) => data.json())
        .then((response) => {
          return response;
        });
    }

    // overwrite with initiateWithAthleteId if set
    if (config.hasOwnProperty('initiateWithAthleteId')) {
      json.active[0].athlete_id = config.initiateWithAthleteId;
    }

    setAthleteId(json.active[0].athlete_id);

    if (config.useActiveToggle && active && json.status !== 'finished') {
      iwdTimeout = setTimeout(
        () => fetchActive(active),
        config.pollRateSeconds ? config.pollRateSeconds * 1000 : 15000
      );
    }
  };

  const changeActiveState = (newState) => {
    clearTimeout(iwdTimeout);
    iwdTimeout = undefined;
    setActive(newState);
  };

  useEffect(() => {
    if (!active) {
      clearTimeout(iwdTimeout);
      iwdTimeout = undefined;
    } else {
      fetchActive(active);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, entrants]);

  useEffect(() => {
    fetch(config.jsonUri, { credentials: 'include' })
      .then((data) => data.json())
      .then((response) => {
        if (response.entrants) {
          setEntrants(response.entrants);
          const values = response.entrants.map((athlete) => {
            const athleteData = athlete.hasOwnProperty('athlete') ? athlete.athlete : athlete;
            return {
              label: (
                <>
                  {config.showSelectAthleteImage && (
                    <CosmosImage
                      class="option-image"
                      image={athleteData.image ? (athleteData.image.profile || athlete_skeleton) : athlete_skeleton}
                      image-preview={athlete_skeleton}
                      image-alt={`image-${athleteData.first_name}-${athleteData.last_name}`} />
                  )}
                  <CosmosText
                    class="option-text"
                    kind="subtle"
                    style={{ textTransform: 'capitalize' }}>
                    <img
                      className="flag"
                      src={CountryFlag(athleteData.nationality.iso3)}
                      alt={athleteData.nationality.iso3}
                    />
                    {`${capitalize(athleteData.first_name)} ${capitalize(athleteData.last_name)}`}
                  </CosmosText>
                </>
              ),
              athlete_id: athlete.athlete_id,

              value: `iwdd-${athlete.athlete_id}`
            };
          });
          setOptions(values);

          if (!config.useActiveToggle) {
            setAthleteId(values[0].athlete_id);
          }
        }
      });

    const dataLayerArgs = {
      dataLayer: {
        event: 'rbVideoTracking',
        eventCategory: 'video',
        eventAction: 'sidebar',
        eventLabel: 'loaded',
        widgetType: `AthleteProfileLite - ${config.trackingTitle || config.navigationTitle
          }`
      }
    };

    TagManager.dataLayer(dataLayerArgs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.jsonUri]);

  useEffect(() => {
    if (athleteId) {
      fetch(config.uriAthlete.replace('{athleteId}', athleteId), {
        credentials: 'include'
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.athlete) {
            const { age, first_name, last_name, id, image, nationality } = data.athlete;
            const { career_stats } = data;

            setSelected({
              ...selected,
              age: age,
              athlete_id: id,
              nationality: nationality.iso3,
              label: `${first_name + ' '} ${last_name}`,
              image_profile: image.profile,
              image_background: image.background,
              value: `iwdd-${id}`,
              career_stats: career_stats
            });
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteId]);

  useEffect(() => {
    if (options) {
      const currentIndex = options.findIndex((option) => option.value === selected.value);
      let newDirection = -1;

      if (
        (currentIndex > page && currentIndex !== options.length - 1) ||
        (currentIndex === options.length - 1 && page === options.length - 2) ||
        (page === options.length - 1 && currentIndex === 0)
      ) {
        newDirection = 1;
      }

      setPage([currentIndex, newDirection]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteId]);

  useEffect(() => {
    if (selected && arrow) {
      tagChange(arrow, selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    // initiate module with athlete id
    if (config.hasOwnProperty('initiateWithAthleteId') && options) {
      const athleteId = getAthleteIdById(options, config.initiateWithAthleteId);
      if (athleteId) {
        setActive(false);
        setAthleteId(athleteId);
      } else {
        console.warn(`athlete id (${config.initiateWithAthleteId}) not found, set first athlete in list`);
        setSelected(options[0]);
      }
    }
  }, [config, options]);

  const variants = {
    enter: (direction) => {
      return {
        x: direction > 0 ? 1000 : -1000,
        opacity: 0
      };
    },
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => {
      return {
        zIndex: 0,
        x: direction < 0 ? 1000 : -1000,
        opacity: 0
      };
    }
  };

  const tagChange = (direction, selected) => {
    const dataLayerArgs = {
      dataLayer: {
        event: 'rbVideoTracking',
        eventCategory: 'video',
        eventAction: 'sidebar',
        eventLabel: `Slider – ${direction}`,
        widgetType: `AthleteProfileLite - ${config.trackingTitle || config.navigationTitle
          }`,
        widgetTypeProfile: `${selected.label}`
      }
    };

    TagManager.dataLayer(dataLayerArgs);
  };

  return (
    <>
      <motion.div
        className="appRow"
        key={config.navigationTitle}
        style={{ marginTop: '-16px' }}>
        {options && options.length && (
          <AnimatePresence initial={false} custom={direction}>
            <div className="appRow full-width">

              {config.useActiveToggle && (
                <div
                  className={style.activeToggleWrapper}
                  style={config.activeTogglePosition || {}}
                  onClick={() => { changeActiveState(!active); }}>
                  {!active && <>{getTrans(autoUpdate)}</>} <OnOffSwitch on={active} />
                </div>
              )}

              <div
                style={{
                  backgroundColor:
                    selected && !selected.image_background ? 'rgba(255,255,255,0.2)' : '',
                  backgroundImage: `url(${selected ? selected.image_background : ''})`,
                  backgroundSize: 'cover',
                  position: 'relative'
                }}>


                <CosmosButton
                  class={style.controlls}
                  style={{ top: config.arrowPostionFromTop }}
                  onClick={() => {
                    let decrement;
                    const athleteIndex = options.findIndex(
                      (option) => option.athlete_id === athleteId
                    );
                    const back = athleteIndex - 1;
                    if (back < 0) {
                      decrement = options[options.length - 1].athlete_id;
                    } else if (back < options.length - 1) {
                      decrement = options[back].athlete_id;
                    }
                    setAthleteId(decrement);
                    setArrow('Previous');
                    setActive(false);
                  }}
                  kind="bare"
                  shape="circle">
                  <CosmosIconArrowLeft slot="icon" style={{ width: '24px', height: '24px' }} />
                </CosmosButton>

                <motion.img
                  key={selected && selected.athlete_id}
                  src={
                    selected && selected.image_profile
                      ? selected.image_profile
                      : athlete_skeleton
                  }
                  custom={direction}
                  variants={variants}
                  transition={{
                    x: { type: 'spring', stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 }
                  }}
                  exit="exit"
                  className={style.image}
                  initial="enter"
                  animate="center" />

                <div
                  className={style.selectboxWrapper}
                  style={config.dropdownPosition || {}}>
                  <ModalSelect
                    headerText={getTrans(selectText).toUpperCase()}
                    className={`mini ${style.select}`}
                    value={{
                      label: getTrans(selectText),
                      value: selected
                        ? selected.value
                        : options
                          ? setSelected(options[0]) || options[0].value
                          : undefined
                    }}
                    buttonShape="pill"
                    buttonWidth="content"
                    showSelectAthleteImage={config.showSelectAthleteImage}
                    options={options}
                    onChange={(value) => {
                      setActive(false);
                      setAthleteId(value.athlete_id);
                      setArrow('');
                    }}/>
                </div>

                <CosmosButton
                  class={`${style.controlls} ${style.right}`}
                  style={{ top: config.arrowPostionFromTop }}
                  onClick={() => {
                    let increment;
                    const athleteIndex = options.findIndex(
                      (option) => option.athlete_id === athleteId
                    );
                    const next = athleteIndex + 1;
                    if (next > options.length - 1) {
                      increment = options[0].athlete_id;
                    } else if (next <= athleteIndex + 1) {
                      increment = options[next].athlete_id;
                    }
                    setAthleteId(increment);
                    setArrow('Next');
                    setActive(false);
                  }}
                  kind="bare"
                  shape="circle">
                  <CosmosIconArrowRight slot="icon" style={{ width: '24px', height: '24px' }} />
                </CosmosButton>

                {selected && (
                  <div style={{
                    background: 'linear-gradient(180deg, rgba(0,15,30,0) 0%, rgba(0,15,30,.4) 78%)',
                    mixBlendMode: 'normal'
                    }}>
                    <div className='appRow mb-0 modest-width'>
                      <div className={style.factMandatoryWrapperTop}>
                        <span className={style.athleteProfileName}>{selected.label}</span>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                          <span className={style.athleteProfileName}>
                            {selected.nationality}
                          </span>
                          <img
                            className={`flag`}
                            src={CountryFlag(selected.nationality)}
                            alt={selected.nationality}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <Image selected={selected} list={options} config={config} />
            </div>
          </AnimatePresence>
        )}
      </motion.div>
    </>
  );
};

export { AthleteProfileLite };
